import { useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '@shared/services/firebase';
import { logError } from '@/lib/log';

// Module-level flag set synchronously by OnboardingFlow's finish/skip handlers.
// Short-circuits the hook so the redirect effect can't race ahead of the
// Firestore snapshot and kick the user back to /welcome.
let onboardingCompletedThisSession = false;

// Persistent fallback for when the Firestore write hasn't reached the server
// before the user reloads (Firestore is memory-only in this app, no IndexedDB
// persistence). Keyed per-uid so another account on the same device starts
// fresh.
const LOCAL_STORAGE_KEY = 'enough_onboarding_completed_uid';

function readLocalCompleted(uid: string): boolean {
    try {
        return localStorage.getItem(LOCAL_STORAGE_KEY) === uid;
    } catch {
        return false;
    }
}

export function markOnboardingCompletedLocally(uid?: string): void {
    onboardingCompletedThisSession = true;
    if (uid) {
        try { localStorage.setItem(LOCAL_STORAGE_KEY, uid); } catch { /* ignore */ }
    }
}

export function clearLocalOnboardingCompletion(): void {
    onboardingCompletedThisSession = false;
    try { localStorage.removeItem(LOCAL_STORAGE_KEY); } catch { /* ignore */ }
}

/**
 * Returns whether the user needs to go through the post-login welcome flow.
 *
 * - `null` while loading (don't redirect yet)
 * - `true` if the user hasn't completed the enough onboarding
 * - `false` if onboarding is done or user is clearly not new
 *
 * Uses `e_onboardingCompleted` (e_ prefix to distinguish from legacy app).
 *
 * Connected accounts no longer count as completion (2026-10-06,
 * CONNECT_FLOW_PLAN.md §7 G/H): a person who closed the app or was bounced
 * through an OAuth redirect mid-flow has accounts but no first session, and
 * `/welcome` now resumes them from the data instead of starting over. The
 * control arm finishes at once when accounts exist, so nobody with a bank is
 * ever asked to connect again.
 */
export function useOnboardingRedirect(uid: string | undefined): boolean | null {
    const [needsOnboarding, setNeedsOnboarding] = useState<boolean | null>(() => {
        if (onboardingCompletedThisSession) return false;
        if (uid && readLocalCompleted(uid)) return false;
        return null;
    });

    useEffect(() => {
        if (!uid) {
            setNeedsOnboarding(null);
            return;
        }

        // Trust the persisted local flag immediately — this covers reloads where
        // the Firestore write got dropped before reaching the server.
        if (readLocalCompleted(uid)) {
            setNeedsOnboarding(false);
        }

        // The userContent doc is REACTIVE so no completion signal can be missed
        // by a timing race (the bug where finishing paid onboarding wrote the
        // flag, navigated home, then got bounced back before the snapshot
        // caught up).
        let docLoaded = false;
        let docComplete = false;

        const evaluate = () => {
            // Any completion signal wins.
            if (onboardingCompletedThisSession || readLocalCompleted(uid) || docComplete) {
                setNeedsOnboarding(false);
                return;
            }
            // Only decide "needs onboarding" once the doc snapshot has actually
            // loaded — otherwise we could redirect before the signals arrive.
            if (docLoaded) {
                setNeedsOnboarding(true);
            }
        };

        const unsubDoc = onSnapshot(doc(db, 'userContent', uid), (snapshot) => {
            docLoaded = true;
            const data = snapshot.data();
            docComplete = !!(data && (
                data.e_onboardingCompleted ||
                // Legacy: user completed old onboarding or seed cards
                data.onboardingCompleted ||
                data.onboardingSeedState?.['mock-reflection'] === 'completed' ||
                data.onboardingSeedState?.['plaid-invitation'] === 'completed'
            ));
            evaluate();
        }, (error) => {
            // Without this callback a listener error left needsOnboarding null
            // forever — an unrecoverable boot spinner with zero telemetry.
            // Surface it in Sentry; the redirect decision itself stays pending
            // (the gate telemetry counts the stall).
            logError(error, { context: 'onboarding_redirect_user_content' });
        });

        return () => { unsubDoc(); };
    }, [uid]);

    // The local flag is read at render time as well: finishing onboarding sets
    // it and navigates home in the same tick, before the doc snapshot that
    // confirms the write can arrive. Without this the host's redirect effect
    // saw a stale `true` and bounced the person straight back into /welcome.
    if (uid && (onboardingCompletedThisSession || readLocalCompleted(uid))) return false;
    return needsOnboarding;
}
